import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { KeyRound, RefreshCw, Search, Copy, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PortalOTPInbox() {
  const [users, setUsers] = useState([]);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/api/whatsapp/portal-otp-inbox${phone ? `?phone=${encodeURIComponent(phone.trim())}` : ''}`;
      const r = await axios.get(url, { headers });
      setUsers(r.data?.users || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load OTPs');
    }
    setLoading(false);
  }, [phone]); // eslint-disable-line

  useEffect(() => { load(); }, []); // eslint-disable-line

  const copyOtp = (otp) => {
    navigator.clipboard.writeText(otp);
    toast.success(`Copied ${otp}`);
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-IN'); }
    catch { return iso; }
  };

  return (
    <div className="container mx-auto py-6 max-w-4xl space-y-4" data-testid="otp-inbox-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <KeyRound className="h-6 w-6" /> Portal OTP Inbox
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Live view of recent portal user OTPs — for testing customer login flows.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              placeholder="Filter by phone..."
              value={phone}
              onChange={e => setPhone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && load()}
              className="pl-7 w-60"
              data-testid="otp-phone-filter"
            />
          </div>
          <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="otp-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Recent OTPs ({users.length})</CardTitle>
          <CardDescription>Sorted by latest OTP timestamp.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {users.length === 0 ? (
            <div className="p-12 text-center text-slate-400">No OTPs yet. Trigger one from the portal login screen.</div>
          ) : (
            <div className="divide-y">
              {users.map((u, i) => (
                <div key={i} className="p-3 grid grid-cols-12 gap-2 items-center hover:bg-slate-50 text-sm" data-testid={`otp-row-${u.phone}`}>
                  <div className="col-span-3">
                    <p className="font-medium flex items-center gap-1">
                      <Phone className="h-3.5 w-3.5 text-slate-400" /> {u.phone}
                    </p>
                    {u.name && <p className="text-xs text-slate-500 truncate">{u.name}</p>}
                  </div>
                  <div className="col-span-3 text-xs">
                    <p className="text-slate-500">Linked: {(u.linked_customer_ids || []).length}</p>
                    {u.email && <p className="truncate text-slate-400">{u.email}</p>}
                  </div>
                  <div className="col-span-3 text-xs">
                    <p className="text-slate-400">OTP at: {fmt(u.last_otp_at)}</p>
                    <p className="text-slate-400">Login at: {fmt(u.last_login_at)}</p>
                  </div>
                  <div className="col-span-3 flex items-center gap-2 justify-end">
                    {u.last_otp ? (
                      <>
                        <Badge variant="outline" className="text-base font-mono px-2 py-0.5 border-amber-300 bg-amber-50 text-amber-700" data-testid={`otp-value-${u.phone}`}>
                          {u.last_otp}
                        </Badge>
                        <Button size="sm" variant="ghost" onClick={() => copyOtp(u.last_otp)} data-testid={`otp-copy-${u.phone}`}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">No OTP yet</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
